import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEnrollments } from '../hooks/useEnrollments';
import { useProgress } from '../hooks/useProgress';
import CourseCard from '../components/ui/CourseCard';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiBook, FiTrendingUp, FiAward, FiClock, FiPlay } = FiIcons;

const Dashboard = () => {
  const { profile } = useAuth();
  const { enrollments, loading } = useEnrollments();

  // Get progress for all enrolled courses
  const enrolledCourses = enrollments.map(enrollment => enrollment.courses);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  const stats = [
    {
      name: 'Enrolled Courses',
      value: enrollments.length,
      icon: FiBook,
      color: 'text-blue-600 bg-blue-100'
    },
    {
      name: 'Completed Courses', 
      value: 0, // Calculate based on progress
      icon: FiAward,
      color: 'text-green-600 bg-green-100'
    },
    {
      name: 'Learning Streak',
      value: '5 days',
      icon: FiTrendingUp,
      color: 'text-purple-600 bg-purple-100'
    },
    {
      name: 'Total Study Time',
      value: '24h',
      icon: FiClock,
      color: 'text-orange-600 bg-orange-100'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome back, {profile?.name}!
          </h1>
          <p className="text-gray-600">
            Continue your nutrition learning journey
          </p>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={stat.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 + index * 0.1 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
            >
              <div className="flex items-center">
                <div className={`p-3 rounded-lg ${stat.color}`}>
                  <SafeIcon icon={stat.icon} className="w-6 h-6" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Continue Learning Section */}
        {enrollments.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-8"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Continue Learning</h2>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <ContinueLearningCard enrollment={enrollments[0]} />
            </div>
          </motion.div>
        )}

        {/* My Courses */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">My Courses</h2>
          
          {enrollments.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <SafeIcon icon={FiBook} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No courses yet</h3>
              <p className="text-gray-600 mb-6">
                Start your nutrition journey by enrolling in your first course.
              </p>
              <Link
                to="/courses"
                className="inline-flex items-center space-x-2 bg-nutrition-600 hover:bg-nutrition-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                <span>Browse Courses</span>
                <SafeIcon icon={FiBook} className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {enrollments.map((enrollment, index) => (
                <motion.div
                  key={enrollment.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 + index * 0.1 }}
                >
                  <CourseProgressCard enrollment={enrollment} />
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

// Component for "Continue Learning" section
const ContinueLearningCard = ({ enrollment }) => {
  const { getCourseProgress, getNextIncompleteLesson } = useProgress(enrollment.courses.id);
  const { course, modules } = enrollment.courses;

  // Mock modules for now - in real app, you'd fetch this
  const mockModules = [
    {
      id: '1',
      title: 'Introduction to Nutrition',
      lessons: [
        { id: '1', title: 'What is Nutrition?' },
        { id: '2', title: 'Macronutrients Overview' }
      ]
    }
  ];

  const progress = getCourseProgress(mockModules);
  const nextLesson = getNextIncompleteLesson(mockModules);

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-start space-x-4">
        {enrollment.courses.cover_image_url && (
          <img
            src={enrollment.courses.cover_image_url}
            alt={enrollment.courses.title}
            className="w-16 h-16 object-cover rounded-lg"
          />
        )}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            {enrollment.courses.title}
          </h3>
          <p className="text-sm text-gray-600 mb-2">
            {nextLesson 
              ? `Next: ${nextLesson.lesson.title}`
              : 'Course completed!'
            }
          </p>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-nutrition-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress.percentage}%` }}
                />
              </div>
              <span className="text-sm text-gray-600">{progress.percentage}%</span>
            </div>
          </div>
        </div>
      </div>
      
      <Link
        to={nextLesson 
          ? `/course/${enrollment.courses.slug}/lesson/${nextLesson.lesson.id}`
          : `/course/${enrollment.courses.slug}`
        }
        className="flex items-center space-x-2 bg-nutrition-600 hover:bg-nutrition-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
      >
        <SafeIcon icon={FiPlay} className="w-4 h-4" />
        <span>{nextLesson ? 'Continue' : 'Review'}</span>
      </Link>
    </div>
  );
};

// Component for course cards with progress
const CourseProgressCard = ({ enrollment }) => {
  const { getCourseProgress } = useProgress(enrollment.courses.id);
  
  // Mock modules for now - in real app, you'd fetch this
  const mockModules = [
    {
      id: '1',
      title: 'Introduction to Nutrition',
      lessons: [
        { id: '1', title: 'What is Nutrition?' },
        { id: '2', title: 'Macronutrients Overview' }
      ]
    }
  ];

  const progress = getCourseProgress(mockModules);

  return (
    <CourseCard
      course={enrollment.courses}
      showProgress={true}
      progress={progress}
    />
  );
};

export default Dashboard;